import React, { useContext, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDatabase } from '@fortawesome/free-solid-svg-icons';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import type { FieldTypeDefinition, FieldRendererProps, FieldEditorProps } from '../registry';
import { InjectedDataContext } from '../../context/InjectedDataContext';

interface InjectedOption {
  key: string;
  label: string;
}

const OPTIONS: InjectedOption[] = [
  { key: 'admin_time_past_week', label: 'Injected AdminTime entries for the past week' },
  { key: 'allergies', label: 'Injected Allergies' },
  { key: 'billable_service_time_past_week', label: 'Injected Billable Service time for past week' },
  { key: 'client_external_providers', label: 'Injected Client External Providers' },
  { key: 'client_liability', label: 'Injected Client Liability' },
  { key: 'contacts', label: 'Injected Contacts' },
  { key: 'data_dictionary_field', label: 'Injected Data Dictionary Field' },
  { key: 'diagnosis', label: 'Injected Diagnosis' },
  { key: 'diagnosis_full', label: 'Injected Diagnosis Full' },
  { key: 'family', label: 'Injected Family' },
  { key: 'lab_results', label: 'Injected Lab Results' },
  { key: 'medical_profile_details', label: 'Injected Medical Profile Details' },
  { key: 'medical_profile_header', label: 'Injected Medical Profile Header' },
  { key: 'medications', label: 'Injected Medications' },
  { key: 'medications_full', label: 'Injected Medications Full' },
  { key: 'medications_full_erx_only', label: 'Injected Medications Full eRx Only' },
  { key: 'non_billable_service_time_past_week', label: 'Injected Non-Billable Service time for past week' },
  { key: 'reconciled_medications', label: 'Injected Reconciled Medications' },
  { key: 'timesheet_past_week', label: 'Injected TimeSheet entries for the past week' },
  { key: 'tx_plan', label: 'Injected Tx Plan' },
  { key: 'timesheet_week_start_end', label: 'Injected week start/end for TimeSheet' },
];

/** Full list of injection type keys and display labels — export for host-side reference. */
export { OPTIONS as INJECTED_DATA_OPTIONS };
export type { InjectedOption as InjectedDataOption };

function statusBox(color: 'subtle' | 'error', children: React.ReactNode) {
  return (
    <div
      style={{
        padding: 'var(--kendo-spacing-2) var(--kendo-spacing-3)',
        borderRadius: 'var(--kendo-border-radius-md)',
        border: `1px dashed var(--kendo-color-${color === 'error' ? 'error' : 'border'})`,
        color: `var(--kendo-color-${color === 'error' ? 'error' : 'subtle'})`,
        fontSize: 'var(--kendo-font-size-sm)',
      }}
    >
      {children}
    </div>
  );
}

function InjectedDataRenderer({ field, value, onChange }: FieldRendererProps) {
  const resolveInjectedData = useContext(InjectedDataContext);
  const injectionType = field.injectedData?.injectionType;
  const fetchedRef = useRef(false);
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const option = OPTIONS.find((o) => o.key === injectionType);

  useEffect(() => {
    // Re-use a snapshot already captured in answers (e.g. form re-opened with saved answers)
    if (typeof value === 'string' && value) { setContent(value); return; }
    if (!injectionType || !resolveInjectedData || fetchedRef.current) return;
    fetchedRef.current = true;
    setLoading(true);
    setFetchError(null);
    resolveInjectedData(injectionType)
      .then((data) => { setContent(data); onChange(data); })
      .catch(() => setFetchError('Failed to load injected data.'))
      .finally(() => setLoading(false));
  // injectionType and resolveInjectedData are the only deps that should re-trigger a fetch
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [injectionType, resolveInjectedData]);

  if (!injectionType) {
    return statusBox('subtle', "No injection type selected. Set it in the field's Settings tab.");
  }

  // No callback — running in builder preview or a renderer without injected data support
  if (!resolveInjectedData) {
    return statusBox('subtle', `[ ${option?.label ?? injectionType} ]`);
  }

  if (loading) {
    return statusBox('subtle', `Loading ${option?.label ?? injectionType}…`);
  }

  if (fetchError) {
    return statusBox('error', fetchError);
  }

  if (!content) {
    return statusBox('subtle', 'No data available.');
  }

  // Content is provided by the trusted host app — same trust boundary as LabelField HTML.
  return <div dangerouslySetInnerHTML={{ __html: content }} />;
}

function InjectedDataPropertyEditor({ field, onChange }: FieldEditorProps) {
  const current = OPTIONS.find((o) => o.key === field.injectedData?.injectionType) ?? null;

  return (
    <div>
      <label
        style={{
          fontSize: 'var(--kendo-font-size-xs)',
          fontWeight: 'var(--kendo-font-weight-bold)',
          color: 'var(--kendo-color-subtle)',
          display: 'block',
          marginBottom: 'var(--kendo-spacing-1)',
        }}
      >
        Injection Type
      </label>
      <DropDownList
        data={OPTIONS}
        textField="label"
        dataItemKey="key"
        value={current}
        defaultItem={{ key: '', label: 'Select a type…' }}
        onChange={(e) => { if (e.value?.key) onChange({ injectedData: { injectionType: e.value.key } }); }}
        style={{ width: '100%' }}
      />
    </div>
  );
}

export const definition: FieldTypeDefinition = {
  key: 'INJ',
  label: 'Injected Data',
  category: 'advanced',
  icon: <FontAwesomeIcon icon={faDatabase} fixedWidth />,
  defaultProps: { label: 'Injected Data', isRequired: false },
  Renderer: InjectedDataRenderer,
  PropertyEditor: InjectedDataPropertyEditor,
};
